import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Trash2, Edit, Upload, FileText, Check, RotateCcw, X, Download, Building2, Eye, FileSignature, Mail, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

const emptyItem = { description: '', quantity: 1, unit: '', unit_price: 0, btw: 21 };

const emptyInvoice = {
  supplier_id: '', supplier_name: '', invoice_number: '', invoice_date: '',
  due_date: '', amount_excl_btw: 0, btw_rate: 21, btw_status: 'normal',
  description: '', category: 'material', notes: '',
  is_self_billed: false, items: [],
  supplier_address: '', supplier_kvk: '', supplier_iban: '', supplier_email: '', supplier_phone: '',
};

const emptySupplier = { name: '', email: '', phone: '', address: '', kvk: '', iban: '', notes: '' };

const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PurchaseInvoices = ({ user }) => {
  const readOnly = user?.role === 'boekhouder';
  const [tab, setTab] = useState('invoices'); // invoices | suppliers
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({ status: 'all', supplier_id: 'all', start_date: '', end_date: '' });

  // Invoice form
  const [showInvForm, setShowInvForm] = useState(false);
  const [editingInvId, setEditingInvId] = useState(null);
  const [invForm, setInvForm] = useState({ ...emptyInvoice });
  const pdfInputRef = useRef(null);

  // Supplier form
  const [showSupForm, setShowSupForm] = useState(false);
  const [editingSupId, setEditingSupId] = useState(null);
  const [supForm, setSupForm] = useState({ ...emptySupplier });

  useEffect(() => { fetchSuppliers(); }, []);
  useEffect(() => { fetchInvoices(); }, [filters.status, filters.supplier_id, filters.start_date, filters.end_date]);

  const fetchSuppliers = async () => {
    try { const res = await axiosInstance.get('/suppliers'); setSuppliers(res.data); }
    catch { toast.error('Fout bij laden leveranciers'); }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.supplier_id !== 'all') params.supplier_id = filters.supplier_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const res = await axiosInstance.get('/purchase-invoices', { params });
      setInvoices(res.data);
    } catch { toast.error('Fout bij laden inkoopfacturen'); }
    finally { setLoading(false); }
  };

  // ----- Supplier handlers -----
  const handleSupSubmit = async () => {
    if (!supForm.name) { toast.error('Naam is verplicht'); return; }
    try {
      if (editingSupId) {
        await axiosInstance.put(`/suppliers/${editingSupId}`, supForm);
        toast.success('Leverancier bijgewerkt');
      } else {
        await axiosInstance.post('/suppliers', supForm);
        toast.success('Leverancier aangemaakt');
      }
      setShowSupForm(false); setEditingSupId(null); setSupForm({ ...emptySupplier });
      fetchSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fout bij opslaan'); }
  };

  const handleSupDelete = async (id) => {
    if (!window.confirm('Leverancier verwijderen?')) return;
    try { await axiosInstance.delete(`/suppliers/${id}`); toast.success('Leverancier verwijderd'); fetchSuppliers(); }
    catch { toast.error('Fout bij verwijderen'); }
  };

  // ----- Invoice handlers -----
  const handleInvSupplierChange = (sid) => {
    const sup = suppliers.find(s => s.id === sid);
    setInvForm(f => ({
      ...f,
      supplier_id: sid,
      supplier_name: sup?.name || '',
      supplier_address: sup?.address || '',
      supplier_kvk: sup?.kvk || '',
      supplier_iban: sup?.iban || '',
      supplier_email: sup?.email || '',
      supplier_phone: sup?.phone || '',
    }));
  };

  const isVerlegd = invForm.btw_status === 'verlegd';
  const calcBtw = () => isVerlegd ? 0 : (parseFloat(invForm.amount_excl_btw) || 0) * (parseFloat(invForm.btw_rate) || 0) / 100;
  const calcTotal = () => (parseFloat(invForm.amount_excl_btw) || 0) + calcBtw();

  // Line items handlers (self-bill mode)
  const calcItemsTotals = () => {
    const items = invForm.items || [];
    let subtotal = 0;
    const btwByRate = {};
    items.forEach(it => {
      const lt = (parseFloat(it.quantity)||0) * (parseFloat(it.unit_price)||0);
      subtotal += lt;
      if (!isVerlegd && (parseFloat(it.btw)||0) > 0) {
        const r = parseFloat(it.btw);
        btwByRate[`${r}%`] = (btwByRate[`${r}%`] || 0) + lt * r/100;
      }
    });
    const totalBtw = isVerlegd ? 0 : Object.values(btwByRate).reduce((a,b)=>a+b, 0);
    return { subtotal, btwByRate, totalBtw, total: subtotal + totalBtw };
  };

  const addItem = () => setInvForm(f => ({ ...f, items: [...(f.items||[]), { ...emptyItem }] }));
  const updateItem = (i, k, v) => setInvForm(f => {
    const items = [...(f.items||[])];
    items[i] = { ...items[i], [k]: v };
    return { ...f, items };
  });
  const removeItem = (i) => setInvForm(f => ({ ...f, items: (f.items||[]).filter((_, idx) => idx !== i) }));

  const handleInvSubmit = async () => {
    if (!invForm.supplier_name) { toast.error('Selecteer of vul leverancier in'); return; }
    if (!invForm.invoice_number) { toast.error('Factuurnummer is verplicht'); return; }
    if (!invForm.invoice_date) { toast.error('Factuurdatum is verplicht'); return; }
    if (invForm.is_self_billed && (!invForm.items || invForm.items.length === 0)) {
      toast.error('Voeg minimaal één regel toe voor zelf-gemaakte factuur');
      return;
    }
    const payload = {
      ...invForm,
      amount_excl_btw: parseFloat(invForm.amount_excl_btw) || 0,
      btw_rate: parseFloat(invForm.btw_rate) || 0,
      btw_amount: calcBtw(),
      total_incl: calcTotal(),
      items: (invForm.items || []).map(it => ({
        description: it.description || '',
        quantity: parseFloat(it.quantity) || 1,
        unit: it.unit || '',
        unit_price: parseFloat(it.unit_price) || 0,
        btw: parseFloat(it.btw) || 0,
      })),
    };
    try {
      if (editingInvId) {
        await axiosInstance.put(`/purchase-invoices/${editingInvId}`, payload);
        toast.success('Inkoopfactuur bijgewerkt');
      } else {
        await axiosInstance.post('/purchase-invoices', payload);
        toast.success('Inkoopfactuur aangemaakt');
      }
      resetInvForm();
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fout bij opslaan'); }
  };

  const resetInvForm = () => {
    setShowInvForm(false); setEditingInvId(null); setInvForm({ ...emptyInvoice });
  };

  const openPdf = async (inv) => {
    const win = window.open('', '_blank');
    try {
      const r = await axiosInstance.get(`/purchase-invoices/${inv.id}/pdf-url`);
      if (win) win.location.href = r.data.url; else window.open(r.data.url, '_blank');
    } catch (e) {
      if (win) win.close();
      toast.error(e.response?.data?.detail || 'PDF openen mislukt');
    }
  };

  const handleInvEdit = (inv) => {
    setEditingInvId(inv.id);
    setInvForm({
      supplier_id: inv.supplier_id || '',
      supplier_name: inv.supplier_name || '',
      invoice_number: inv.invoice_number || '',
      invoice_date: inv.invoice_date || '',
      due_date: inv.due_date || '',
      amount_excl_btw: inv.amount_excl_btw || 0,
      btw_rate: inv.btw_rate || 21,
      btw_status: inv.btw_status || 'normal',
      description: inv.description || '',
      category: inv.category || 'material',
      notes: inv.notes || '',
      is_self_billed: inv.is_self_billed || false,
      items: inv.items || [],
      supplier_address: inv.supplier_address || '',
      supplier_kvk: inv.supplier_kvk || '',
      supplier_iban: inv.supplier_iban || '',
      supplier_email: inv.supplier_email || '',
      supplier_phone: inv.supplier_phone || '',
    });
    setShowInvForm(true);
  };

  const handleInvDelete = async (id) => {
    if (!window.confirm('Inkoopfactuur verwijderen?')) return;
    try { await axiosInstance.delete(`/purchase-invoices/${id}`); toast.success('Verwijderd'); fetchInvoices(); }
    catch { toast.error('Fout bij verwijderen'); }
  };

  const handleTogglePaid = async (inv) => {
    try {
      if (inv.status === 'paid') {
        await axiosInstance.put(`/purchase-invoices/${inv.id}/mark-unpaid`);
        toast.success('Gemarkeerd als niet betaald');
      } else {
        await axiosInstance.put(`/purchase-invoices/${inv.id}/mark-paid`);
        toast.success('Gemarkeerd als betaald');
      }
      fetchInvoices();
    } catch { toast.error('Fout bij bijwerken status'); }
  };

  const handlePdfUpload = async (invId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('pdf', file);
    try {
      await axiosInstance.post(`/purchase-invoices/${invId}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('PDF geüpload');
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fout bij uploaden'); }
  };

  // Totals (imported items uit e-mail hebben total_incl_btw i.p.v. total_incl)
  const totalOf = (i) => Number(i.total_incl ?? i.total_incl_btw) || 0;
  const totalPending = invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + totalOf(i), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + totalOf(i), 0);
  const manualInvoices = invoices.filter(i => !i.auto_imported);
  const importedInvoices = invoices.filter(i => i.auto_imported);

  const handleConvertToExpense = async (inv) => {
    const name = inv.supplier_name || inv.vendor_name || 'deze factuur';
    if (!window.confirm(`"${name}" (${fmtEuro(totalOf(inv))}) verplaatsen naar Uitgaven?`)) return;
    try {
      const r = await axiosInstance.post(`/purchase-invoices/${inv.id}/convert-to-expense`);
      toast.success(r.data.message || 'Verplaatst naar Uitgaven');
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verplaatsen mislukt'); }
  };

  // PDF Preview
  const [previewInv, setPreviewInv] = useState(null);

  const downloadPdf = (inv) => {
    const el = document.getElementById('pi-pdf-content');
    if (!el) return;
    html2pdf().set({
      margin: 0.5,
      filename: `Inkoopfactuur_${inv.invoice_number || inv.id}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    }).from(el).save();
  };

  const lineItemsTotals = calcItemsTotals();

  const renderInvCard = (inv) => (
    <Card key={inv.id} className={`bg-white shadow-sm border-l-4 ${inv.status === 'paid' ? 'border-l-green-500' : 'border-l-orange-500'} border-gray-200`} data-testid={`pi-${inv.id}`}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-gray-800">{inv.invoice_number}</p>
                {inv.is_self_billed && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">SELF-BILL</span>}
                {inv.btw_status === 'verlegd' && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">BTW VERLEGD</span>}
                {inv.auto_imported && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">UIT E-MAIL</span>}
              </div>
              <p className="text-xs text-gray-400">{inv.invoice_date}</p>
            </div>
            <div>
              <p className="font-medium text-gray-800">{inv.supplier_name || inv.vendor_name}</p>
              <p className="text-xs text-gray-500">{inv.description}</p>
            </div>
            <div className="text-xs text-gray-500">
              {inv.category && <span className="bg-gray-100 px-2 py-1 rounded mr-2">{inv.category}</span>}
              {inv.due_date && <span>Vervalt: {inv.due_date}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-lg font-bold text-gray-800">{fmtEuro(inv.total_incl ?? inv.total_incl_btw)}</p>
              <span className={`text-xs px-2 py-1 rounded ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                {inv.status === 'paid' ? `Betaald ${inv.paid_date || ''}` : 'Te betalen'}
              </span>
            </div>
            <div className="flex gap-1">
              {!readOnly && <Button variant="ghost" size="sm" onClick={() => handleConvertToExpense(inv)} className="text-[#1a5276]" title="Verplaats naar Uitgaven" data-testid={`convert-to-expense-${inv.id}`}>
                <ArrowRightLeft className="w-4 h-4" />
              </Button>}
              {inv.is_self_billed && (
                <Button variant="ghost" size="sm" onClick={() => setPreviewInv(inv)} className="text-purple-600" title="PDF preview" data-testid={`preview-pi-${inv.id}`}>
                  <Eye className="w-4 h-4" />
                </Button>
              )}
              {!readOnly && <Button variant="ghost" size="sm" onClick={() => handleTogglePaid(inv)} className={inv.status === 'paid' ? 'text-orange-600' : 'text-green-600'} title={inv.status === 'paid' ? 'Markeer als niet betaald' : 'Markeer als betaald'} data-testid={`toggle-paid-${inv.id}`}>
                {inv.status === 'paid' ? <RotateCcw className="w-4 h-4" /> : <Check className="w-4 h-4" />}
              </Button>}
              {!readOnly && <Button variant="ghost" size="sm" onClick={() => {
                pdfInputRef.current.dataset.invId = inv.id;
                pdfInputRef.current.click();
              }} className="text-blue-600" title="Upload PDF" data-testid={`upload-pdf-${inv.id}`}>
                <Upload className="w-4 h-4" />
              </Button>}
              {(inv.pdf_url || inv.import_source?.pdf_url) && (
                <button type="button" onClick={() => openPdf(inv)} className="inline-flex items-center justify-center w-9 h-9 hover:bg-gray-100 rounded text-purple-600" title="PDF bekijken" data-testid={`view-pdf-${inv.id}`}>
                  <Download className="w-4 h-4" />
                </button>
              )}
              {!readOnly && <Button variant="ghost" size="sm" onClick={() => handleInvEdit(inv)} className="text-gray-600" title="Bewerken"><Edit className="w-4 h-4" /></Button>}
              {!readOnly && <Button variant="ghost" size="sm" onClick={() => handleInvDelete(inv.id)} className="text-red-500" title="Verwijderen"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-800">Inkoopfacturen</h1>
          <p className="text-sm text-gray-500 mt-1">Facturen die u ontvangt van leveranciers</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={tab === 'invoices' ? 'default' : 'outline'}
            onClick={() => setTab('invoices')}
            className={tab === 'invoices' ? 'bg-[#3da5a5] hover:bg-[#2d8585]' : ''}
            data-testid="tab-invoices"
          >
            <FileText className="w-4 h-4 mr-2" /> Facturen
          </Button>
          <Button
            variant={tab === 'suppliers' ? 'default' : 'outline'}
            onClick={() => setTab('suppliers')}
            className={tab === 'suppliers' ? 'bg-[#3da5a5] hover:bg-[#2d8585]' : ''}
            data-testid="tab-suppliers"
          >
            <Building2 className="w-4 h-4 mr-2" /> Leveranciers
          </Button>
        </div>
      </div>

      {tab === 'invoices' && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-0">
              <CardContent className="py-5">
                <p className="text-orange-100 text-sm">Te betalen</p>
                <p className="text-3xl font-bold text-white">{fmtEuro(totalPending)}</p>
                <p className="text-orange-100 text-xs">{invoices.filter(i => i.status !== 'paid').length} factu(u)r(en)</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 border-0">
              <CardContent className="py-5">
                <p className="text-green-100 text-sm">Betaald</p>
                <p className="text-3xl font-bold text-white">{fmtEuro(totalPaid)}</p>
                <p className="text-green-100 text-xs">{invoices.filter(i => i.status === 'paid').length} factu(u)r(en)</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 border-0">
              <CardContent className="py-5">
                <p className="text-blue-100 text-sm">Totaal</p>
                <p className="text-3xl font-bold text-white">{fmtEuro(totalPending + totalPaid)}</p>
                <p className="text-blue-100 text-xs">{invoices.length} factu(u)r(en)</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters + New */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-gray-700">Status</Label>
                  <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      <SelectItem value="pending">Te betalen</SelectItem>
                      <SelectItem value="paid">Betaald</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Leverancier</Label>
                  <Select value={filters.supplier_id} onValueChange={(v) => setFilters({ ...filters, supplier_id: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="filter-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Van datum</Label>
                  <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                <div>
                  <Label className="text-gray-700">Tot datum</Label>
                  <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                {!readOnly && <Button onClick={() => { resetInvForm(); setShowInvForm(true); }} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="new-purchase-invoice-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nieuwe Factuur
                </Button>}
              </div>
            </CardContent>
          </Card>

          {/* Form */}
          {showInvForm && (
            <Card className="bg-white shadow-sm border-gray-200" data-testid="purchase-invoice-form">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">{editingInvId ? 'Inkoopfactuur bewerken' : 'Nieuwe Inkoopfactuur'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetInvForm}><X className="w-4 h-4" /></Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Self-billing toggle */}
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="self-billed"
                    checked={invForm.is_self_billed}
                    onChange={(e) => setInvForm(f => ({ ...f, is_self_billed: e.target.checked, items: e.target.checked && (f.items?.length || 0) === 0 ? [{ ...emptyItem }] : f.items }))}
                    className="w-5 h-5 mt-0.5"
                    data-testid="self-billed-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="self-billed" className="text-amber-900 font-semibold cursor-pointer flex items-center gap-2">
                      <FileSignature className="w-4 h-4" />
                      Factuur opmaken namens leverancier (self-billing)
                    </Label>
                    <p className="text-xs text-amber-700 mt-0.5">Vink aan als u zelf de factuur opstelt namens de leverancier (regel-voor-regel). Anders alleen registreer ontvangen factuur.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700">Leverancier laden</Label>
                    <Select value={invForm.supplier_id || ''} onValueChange={handleInvSupplierChange}>
                      <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="pi-supplier-select"><SelectValue placeholder="Kies een leverancier..." /></SelectTrigger>
                      <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Leverancier naam *</Label>
                    <Input value={invForm.supplier_name} onChange={e => setInvForm(f => ({ ...f, supplier_name: e.target.value }))} className="bg-white border-gray-200" data-testid="pi-supplier-name" />
                  </div>
                </div>

                {/* Self-billing extra supplier fields */}
                {invForm.is_self_billed && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-gray-50 p-3 rounded-lg">
                    <div className="md:col-span-3">
                      <Label className="text-gray-700 text-xs">Leverancier adres</Label>
                      <Input value={invForm.supplier_address} onChange={e => setInvForm(f => ({ ...f, supplier_address: e.target.value }))} className="bg-white border-gray-200" />
                    </div>
                    <div><Label className="text-gray-700 text-xs">KVK</Label><Input value={invForm.supplier_kvk} onChange={e => setInvForm(f => ({ ...f, supplier_kvk: e.target.value }))} className="bg-white border-gray-200" /></div>
                    <div><Label className="text-gray-700 text-xs">IBAN</Label><Input value={invForm.supplier_iban} onChange={e => setInvForm(f => ({ ...f, supplier_iban: e.target.value }))} className="bg-white border-gray-200" /></div>
                    <div><Label className="text-gray-700 text-xs">Email</Label><Input value={invForm.supplier_email} onChange={e => setInvForm(f => ({ ...f, supplier_email: e.target.value }))} className="bg-white border-gray-200" /></div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-gray-700">Factuurnummer *</Label>
                    <Input value={invForm.invoice_number} onChange={e => setInvForm(f => ({ ...f, invoice_number: e.target.value }))} className="bg-white border-gray-200" data-testid="pi-number" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Factuurdatum *</Label>
                    <Input type="date" value={invForm.invoice_date} onChange={e => setInvForm(f => ({ ...f, invoice_date: e.target.value }))} className="bg-white border-gray-200" data-testid="pi-date" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Vervaldatum</Label>
                    <Input type="date" value={invForm.due_date} onChange={e => setInvForm(f => ({ ...f, due_date: e.target.value }))} className="bg-white border-gray-200" />
                  </div>
                  <div>
                    <Label className="text-gray-700">Categorie</Label>
                    <Select value={invForm.category} onValueChange={(v) => setInvForm(f => ({ ...f, category: v }))}>
                      <SelectTrigger className="bg-white border-gray-200"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="material">Materiaal</SelectItem>
                        <SelectItem value="transport">Transport</SelectItem>
                        <SelectItem value="equipment">Gereedschap</SelectItem>
                        <SelectItem value="services">Diensten</SelectItem>
                        <SelectItem value="other">Overig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* BTW status selector */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700">BTW behandeling</Label>
                    <Select value={invForm.btw_status} onValueChange={(v) => setInvForm(f => ({ ...f, btw_status: v }))}>
                      <SelectTrigger className="bg-white border-gray-200" data-testid="pi-btw-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normaal BTW</SelectItem>
                        <SelectItem value="verlegd">BTW verlegd (omgekeerde heffing)</SelectItem>
                      </SelectContent>
                    </Select>
                    {isVerlegd && <p className="text-xs text-amber-700 mt-1">⚠️ Bij BTW verlegd is BTW = € 0,00 op factuur. De ontvanger (Cardoso Bouw) verrekent BTW zelf.</p>}
                  </div>
                </div>

                {/* Line items (self-billing) OR simple amount */}
                {invForm.is_self_billed ? (
                  <div className="space-y-3 border border-gray-200 p-3 rounded-lg" data-testid="pi-items-section">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-gray-800">Factuurregels</h4>
                      <Button type="button" size="sm" onClick={addItem} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="add-pi-item">
                        <Plus className="w-3 h-3 mr-1" />Regel toevoegen
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left py-1 px-2 text-gray-600 text-xs">Omschrijving</th>
                            <th className="text-left py-1 px-2 text-gray-600 text-xs w-20">Aantal</th>
                            <th className="text-left py-1 px-2 text-gray-600 text-xs w-20">Eenheid</th>
                            <th className="text-left py-1 px-2 text-gray-600 text-xs w-28">Prijs (€)</th>
                            <th className="text-left py-1 px-2 text-gray-600 text-xs w-20">BTW%</th>
                            <th className="text-right py-1 px-2 text-gray-600 text-xs w-24">Totaal</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(invForm.items || []).map((it, i) => {
                            const lt = (parseFloat(it.quantity)||0) * (parseFloat(it.unit_price)||0);
                            return (
                              <tr key={i} className="border-b border-gray-100">
                                <td className="py-1 px-1"><Input value={it.description} onChange={e => updateItem(i,'description',e.target.value)} className="bg-white border-gray-200 h-8 text-sm" /></td>
                                <td className="py-1 px-1"><Input type="number" step="0.01" value={it.quantity} onChange={e => updateItem(i,'quantity',e.target.value)} className="bg-white border-gray-200 h-8 text-sm" /></td>
                                <td className="py-1 px-1"><Input value={it.unit} onChange={e => updateItem(i,'unit',e.target.value)} placeholder="st/u" className="bg-white border-gray-200 h-8 text-sm" /></td>
                                <td className="py-1 px-1"><Input type="number" step="0.01" value={it.unit_price} onChange={e => updateItem(i,'unit_price',e.target.value)} className="bg-white border-gray-200 h-8 text-sm" /></td>
                                <td className="py-1 px-1">
                                  <Select value={String(it.btw)} onValueChange={(v) => updateItem(i, 'btw', parseFloat(v))} disabled={isVerlegd}>
                                    <SelectTrigger className="bg-white border-gray-200 h-8 text-sm"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="0">0%</SelectItem>
                                      <SelectItem value="9">9%</SelectItem>
                                      <SelectItem value="21">21%</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="py-1 px-2 text-right text-sm font-medium">{fmtEuro(lt)}</td>
                                <td className="py-1 px-1"><Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)} className="text-red-500 h-8 px-2"><Trash2 className="w-3 h-3" /></Button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Totals */}
                    <div className="text-right space-y-1">
                      <p className="text-sm">Subtotaal excl. BTW: <strong>{fmtEuro(lineItemsTotals.subtotal)}</strong></p>
                      {isVerlegd ? (
                        <p className="text-sm text-amber-700">BTW verlegd: <strong>€ 0,00</strong></p>
                      ) : (
                        Object.entries(lineItemsTotals.btwByRate).map(([k,v]) => <p key={k} className="text-sm">BTW {k}: <strong>{fmtEuro(v)}</strong></p>)
                      )}
                      <p className="text-lg font-bold text-[#3da5a5]">Totaal: {fmtEuro(lineItemsTotals.total)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-700">Bedrag excl. BTW</Label>
                      <Input type="number" step="0.01" value={invForm.amount_excl_btw} onChange={e => setInvForm(f => ({ ...f, amount_excl_btw: e.target.value }))} className="bg-white border-gray-200" data-testid="pi-amount" />
                    </div>
                    <div>
                      <Label className="text-gray-700">BTW %</Label>
                      <Select value={String(invForm.btw_rate)} onValueChange={(v) => setInvForm(f => ({ ...f, btw_rate: parseFloat(v) }))} disabled={isVerlegd}>
                        <SelectTrigger className="bg-white border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0%</SelectItem>
                          <SelectItem value="9">9%</SelectItem>
                          <SelectItem value="21">21%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col justify-end">
                      <p className="text-sm text-gray-600">BTW: <strong>{fmtEuro(calcBtw())}</strong></p>
                      <p className="text-lg font-bold text-[#3da5a5]">Totaal incl: {fmtEuro(calcTotal())}</p>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-gray-700">Omschrijving</Label>
                  <Input value={invForm.description} onChange={e => setInvForm(f => ({ ...f, description: e.target.value }))} className="bg-white border-gray-200" />
                </div>
                <div>
                  <Label className="text-gray-700">Notities</Label>
                  <textarea value={invForm.notes} onChange={e => setInvForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm h-16" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleInvSubmit} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="save-pi-btn">
                    {editingInvId ? 'Bijwerken' : 'Aanmaken'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hidden PDF input */}
          <input ref={pdfInputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => {
            const file = e.target.files?.[0];
            const invId = pdfInputRef.current?.dataset?.invId;
            if (file && invId) handlePdfUpload(invId, file);
            e.target.value = '';
          }} />

          {/* List */}
          {loading ? (
            <p className="text-gray-500 text-center py-8">Laden...</p>
          ) : invoices.length === 0 ? (
            <Card className="bg-white shadow-sm border-gray-200"><CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Geen inkoopfacturen gevonden</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-6">
              <div className="space-y-3" data-testid="pi-manual-section">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Zelf aangemaakt ({manualInvoices.length})
                </h3>
                {manualInvoices.length === 0
                  ? <p className="text-xs text-gray-400 pl-6">Geen zelf aangemaakte inkoopfacturen</p>
                  : manualInvoices.map(renderInvCard)}
              </div>
              <div className="space-y-3" data-testid="pi-imported-section">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Uit e-mail geïmporteerd ({importedInvoices.length})
                </h3>
                {importedInvoices.length === 0
                  ? <p className="text-xs text-gray-400 pl-6">Geen facturen uit e-mail — nieuwe imports komen standaard bij Uitgaven terecht en verplaats je daar met één klik hierheen</p>
                  : importedInvoices.map(renderInvCard)}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'suppliers' && (
        <>
          <div className="flex justify-end">
            {!readOnly && <Button onClick={() => { setEditingSupId(null); setSupForm({ ...emptySupplier }); setShowSupForm(true); }} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="new-supplier-btn">
              <Plus className="w-4 h-4 mr-1" /> Nieuwe Leverancier
            </Button>}
          </div>
          {suppliers.length === 0 ? (
            <Card className="bg-white shadow-sm border-gray-200"><CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Geen leveranciers gevonden</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.map(s => (
                <Card key={s.id} className="bg-white shadow-sm border-gray-200">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-gray-800 text-lg">{s.name}</CardTitle>
                      {!readOnly && <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingSupId(s.id); setSupForm(s); setShowSupForm(true); }} className="text-blue-600" data-testid={`edit-supplier-${s.id}`}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleSupDelete(s.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-1 text-sm text-gray-600">
                    {s.email && <p>📧 {s.email}</p>}
                    {s.phone && <p>📞 {s.phone}</p>}
                    {s.address && <p>📍 {s.address}</p>}
                    {s.kvk && <p>KVK: {s.kvk}</p>}
                    {s.iban && <p>IBAN: {s.iban}</p>}
                    {s.notes && <p className="text-xs text-gray-400 mt-2">{s.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Dialog open={showSupForm} onOpenChange={(o) => { setShowSupForm(o); if (!o) { setEditingSupId(null); setSupForm({ ...emptySupplier }); } }}>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingSupId ? 'Leverancier bewerken' : 'Nieuwe Leverancier'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Naam *</Label><Input value={supForm.name} onChange={e => setSupForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-50" data-testid="supplier-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input type="email" value={supForm.email} onChange={e => setSupForm(f => ({ ...f, email: e.target.value }))} className="bg-gray-50" /></div>
                  <div><Label>Telefoon</Label><Input value={supForm.phone} onChange={e => setSupForm(f => ({ ...f, phone: e.target.value }))} className="bg-gray-50" /></div>
                </div>
                <div><Label>Adres</Label><Input value={supForm.address} onChange={e => setSupForm(f => ({ ...f, address: e.target.value }))} className="bg-gray-50" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>KVK</Label><Input value={supForm.kvk} onChange={e => setSupForm(f => ({ ...f, kvk: e.target.value }))} className="bg-gray-50" /></div>
                  <div><Label>IBAN</Label><Input value={supForm.iban} onChange={e => setSupForm(f => ({ ...f, iban: e.target.value }))} className="bg-gray-50" /></div>
                </div>
                <div><Label>Notities</Label><textarea value={supForm.notes} onChange={e => setSupForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm h-16" /></div>
                <Button onClick={handleSupSubmit} className="w-full bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="save-supplier-btn">{editingSupId ? 'Bijwerken' : 'Aanmaken'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* PDF Preview Modal — rendered via Portal to escape navbar stacking context */}
      {previewInv && createPortal(
        <div
          className="fixed inset-0 bg-black/60 overflow-y-auto"
          style={{ zIndex: 9999 }}
          onClick={() => setPreviewInv(null)}
          data-testid="pi-pdf-modal"
        >
          {/* Floating close button — always visible, above navbar */}
          <button
            onClick={(e) => { e.stopPropagation(); setPreviewInv(null); }}
            className="fixed top-3 right-3 bg-white hover:bg-red-50 text-red-600 rounded-full p-2 shadow-lg border border-gray-200"
            style={{ zIndex: 10000 }}
            aria-label="Sluiten"
            data-testid="pi-pdf-close-btn"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="min-h-screen flex items-start justify-center p-4 py-16">
            <div
              className="bg-white max-w-4xl w-full rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b bg-white rounded-t-lg sticky top-0 z-10">
                <h2 className="text-xl font-bold text-gray-800">Inkoopfactuur {previewInv.invoice_number}</h2>
                <div className="flex gap-2">
                  <Button onClick={() => downloadPdf(previewInv)} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="pi-pdf-download">
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button variant="ghost" onClick={() => setPreviewInv(null)} data-testid="pi-pdf-close-inline">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div id="pi-pdf-content" style={{ padding: '40px', fontFamily: 'Arial, sans-serif', color: '#222' }}>
              {/* Header: From (Supplier) -> To (Cardoso Bouw) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px', borderBottom: '2px solid #1a5276', paddingBottom: '16px' }}>
                <div>
                  <h2 style={{ color: '#1a5276', fontSize: '20px', margin: '0 0 12px 0' }}>FACTUUR</h2>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Van:</strong></p>
                  <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold' }}>{previewInv.supplier_name}</p>
                  {previewInv.supplier_address && <p style={{ margin: '2px 0', fontSize: '13px' }}>{previewInv.supplier_address}</p>}
                  {previewInv.supplier_kvk && <p style={{ margin: '2px 0', fontSize: '13px' }}>KVK: {previewInv.supplier_kvk}</p>}
                  {previewInv.supplier_iban && <p style={{ margin: '2px 0', fontSize: '13px' }}>IBAN: {previewInv.supplier_iban}</p>}
                  {previewInv.supplier_email && <p style={{ margin: '2px 0', fontSize: '13px' }}>{previewInv.supplier_email}</p>}
                  {previewInv.supplier_phone && <p style={{ margin: '2px 0', fontSize: '13px' }}>{previewInv.supplier_phone}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Aan:</strong></p>
                  <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold' }}>Cardoso Bouw en Onderhoud</p>
                  <p style={{ margin: '2px 0', fontSize: '12px' }}>Vuntuslaan 69, 1231 NR Loosdrecht</p>
                  <p style={{ margin: '2px 0', fontSize: '12px' }}>KVK: 85310646 · BTW-id: NL004077138B58</p>
                  <p style={{ margin: '2px 0', fontSize: '12px' }}>IBAN: NL33INGB0709828160 · BIC: INGBNL2A</p>
                  <p style={{ margin: '2px 0', fontSize: '12px' }}>Mobiel: 0685071009 · info@cardosobouw.nl</p>
                  <p style={{ margin: '12px 0 2px 0', fontSize: '13px' }}><strong>Factuurnummer:</strong> {previewInv.invoice_number}</p>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Factuurdatum:</strong> {previewInv.invoice_date}</p>
                  {previewInv.due_date && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Vervaldatum:</strong> {previewInv.due_date}</p>}
                </div>
              </div>

              {/* Line items */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#1a5276', color: 'white' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Omschrijving</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: '60px' }}>Aantal</th>
                    <th style={{ padding: '8px', textAlign: 'left', width: '60px' }}>Eenheid</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: '90px' }}>Prijs</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: '60px' }}>BTW%</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>Totaal</th>
                  </tr>
                </thead>
                <tbody>
                  {(previewInv.items || []).map((it, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px' }}>{it.description}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{it.quantity}</td>
                      <td style={{ padding: '8px' }}>{it.unit || '-'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{fmtEuro(it.unit_price)}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{previewInv.btw_status === 'verlegd' ? '—' : `${it.btw}%`}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{fmtEuro(it.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: '300px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}><span>Subtotaal excl. BTW:</span><strong>{fmtEuro(previewInv.amount_excl_btw)}</strong></div>
                  {previewInv.btw_status === 'verlegd' ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#c0392b' }}>
                      <span><strong>BTW verlegd (omgekeerde heffing):</strong></span><strong>€ 0,00</strong>
                    </div>
                  ) : (
                    Object.entries(previewInv.btw_totals || {}).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}><span>BTW {k}:</span><strong>{fmtEuro(v)}</strong></div>
                    ))
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #1a5276', marginTop: '8px', fontSize: '15px', fontWeight: 'bold', color: '#1a5276' }}>
                    <span>TOTAAL:</span><span>{fmtEuro(previewInv.total_incl)}</span>
                  </div>
                </div>
              </div>

              {previewInv.btw_status === 'verlegd' && (
                <div style={{ marginTop: '24px', padding: '12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '12px', color: '#92400e' }}>
                  <strong>BTW verlegd (omgekeerde heffing):</strong> De BTW op deze factuur is verlegd naar de ontvanger (Cardoso Bouw en Onderhoud) overeenkomstig art. 12 lid 5 Wet OB 1968.
                </div>
              )}

              {previewInv.notes && (
                <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #ddd', fontSize: '12px', color: '#555' }}>
                  <strong>Notities:</strong> {previewInv.notes}
                </div>
              )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PurchaseInvoices;
